import "firebase/analytics";
import firebase from "firebase/app";
import "firebase/remote-config";
import { OfferValue } from "../../models/custom/offerValue";
import { Helpers } from "../../utilities/helpers";

export default class FirebaseService {
  private static readonly config = {
    apiKey: `${process.env.REACT_APP_FIREBASE_API_KEY}`,
    authDomain: `${process.env.REACT_APP_FIREBASE_AUTH_DOMAIN}`,
    projectId: `${process.env.REACT_APP_FIREBASE_PROJECT_ID}`,
    storageBucket: `${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}`,
    messagingSenderId: `${process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID}`,
    appId: `${process.env.REACT_APP_FIREBASE_APP_ID}`,
    measurementId: `${process.env.REACT_APP_FIREBASE_MEASUREMENT_ID}`,
  };

  private static readonly firebaseApp: firebase.app.App =
    firebase.initializeApp(FirebaseService.config);
  private static readonly analytics: firebase.analytics.Analytics =
    FirebaseService.firebaseApp.analytics();
  private static readonly remoteConfig: firebase.remoteConfig.RemoteConfig =
    FirebaseService.firebaseApp.remoteConfig();
  private static readonly separator = "|";

  constructor() {
    FirebaseService.remoteConfig.defaultConfig = {
      test_key: "test_key",
    };
  }

  public async fetchAndActivateRemoteConfig(): Promise<void> {
    await FirebaseService.firebaseApp.remoteConfig().fetchAndActivate();
  }

  public static getValue(key: string): string | undefined {
    const value = this.remoteConfig.getValue(key).asString();
    return value.trim().length > 0 ? value.trim() : undefined;
  }

  public static logEvent(key: string, params?: { [key: string]: any }): void {
    if (!Helpers.isEnvProd()) {
      console.log(`>> EVENT: ${key}`);
      return;
    }
    this.analytics.logEvent(key, params);
  }

  public static getQuestions(): string[] {
    return this.getStringArray(FirebaseService.question_list);
  }

  public static getAnswers(): string[] {
    return this.getStringArray(FirebaseService.answer_list);
  }

  public static getContactUsSubjects(): string[] {
    return this.getStringArray(FirebaseService.contact_us_subject_list);
  }

  private static getStringArray(key: string): string[] {
    const mergedString = FirebaseService.getValue(key);
    if (!mergedString) return [];
    return mergedString
      .split(FirebaseService.separator)
      .filter((s) => s.trim());
  }

  public static getOfferValues(): OfferValue[] {
    const offerValues = FirebaseService.getValue(FirebaseService.offer_popup);
    if (!offerValues) return [];
    return JSON.parse(offerValues);
  }

  public static getOffer(index: number): OfferValue {
    const offerValues = FirebaseService.getOfferValues();
    return offerValues[index];
  }

  public static getContinue(id: number): string {
    return `user_count_continue_${id}_button_clicked`;
  }

  // KEYS
  public static readonly test_key = "test_key";
  public static readonly answer_list = "answer_list";
  public static readonly cc_button = "cc_button";
  public static readonly cc_payment_title = "cc_payment_title";
  public static readonly cc_ph_card_number = "cc_ph_card_number";
  public static readonly cc_ph_error = "cc_ph_error";
  public static readonly cc_ph_name = "cc_ph_name";
  public static readonly cc_save_card = "cc_save_card";
  public static readonly cc_save_card_text = "cc_save_card_text";
  public static readonly cc_save_card_note = "cc_save_card_note";
  public static readonly cc_saved_cards = "cc_saved_cards";
  public static readonly congrats_highest_extra_reward_prize_button =
    "congrats_highest_extra_reward_prize_button";
  public static readonly congrats_highest_extra_reward_prize_desc =
    "congrats_highest_extra_reward_prize_desc";
  public static readonly congrats_highest_extra_reward_prize_header =
    "congrats_highest_extra_reward_prize_header";
  public static readonly congrats_highest_prize_button =
    "congrats_highest_prize_button";
  public static readonly congrats_highest_prize_desc =
    "congrats_highest_prize_desc";
  public static readonly congrats_highest_prize_header =
    "congrats_highest_prize_header";
  public static readonly congrats_highest_prize_header_v2 =
    "congrats_highest_prize_header_v2";
  public static readonly congrats_lowest_extra_reward_prize_button =
    "congrats_lowest_extra_reward_prize_button";
  public static readonly congrats_lowest_extra_reward_prize_desc =
    "congrats_lowest_extra_reward_prize_desc";
  public static readonly congrats_lowest_extra_reward_prize_header =
    "congrats_lowest_extra_reward_prize_header";
  public static readonly congrats_lowest_prize_button =
    "congrats_lowest_prize_button";
  public static readonly congrats_lowest_prize_desc =
    "congrats_lowest_prize_desc";
  public static readonly congrats_lowest_prize_header =
    "congrats_lowest_prize_header";
  public static readonly congrats_lowest_prize_header_v2 =
    "congrats_lowest_prize_header_v2";
  public static readonly congrats_middle_extra_reward_prize_button =
    "congrats_middle_extra_reward_prize_button";
  public static readonly congrats_middle_extra_reward_prize_desc =
    "congrats_middle_extra_reward_prize_desc";
  public static readonly congrats_middle_extra_reward_prize_header =
    "congrats_middle_extra_reward_prize_header";
  public static readonly congrats_middle_prize_button =
    "congrats_middle_prize_button";
  public static readonly congrats_middle_prize_desc =
    "congrats_middle_prize_desc";
  public static readonly congrats_middle_prize_header =
    "congrats_middle_prize_header";
  public static readonly congrats_middle_prize_header_v2 =
    "congrats_middle_prize_header_v2";
  public static readonly contact_us = "contact_us";
  public static readonly contact_us_button = "contact_us_button";
  public static readonly contact_us_email_error = "contact_us_email_error";
  public static readonly contact_us_invalid_email = "contact_us_invalid_email";
  public static readonly contact_us_mail = "contact_us_mail";
  public static readonly contact_us_message = "contact_us_message";
  public static readonly contact_us_message_error = "contact_us_message_error";
  public static readonly contact_us_name = "contact_us_name";
  public static readonly contact_us_no_result = "contact_us_no_result";
  public static readonly contact_us_no_result_button =
    "contact_us_no_result_button";
  public static readonly contact_us_no_result_message =
    "contact_us_no_result_message";
  public static readonly contact_us_subject = "contact_us_subject";
  public static readonly contact_us_subject_error = "contact_us_subject_error";
  public static readonly contact_us_subject_list = "contact_us_subject_list";
  public static readonly contact_us_successful_button =
    "contact_us_successful_button";
  public static readonly contact_us_successful_text =
    "contact_us_successful_text";
  public static readonly contact_us_successful_title =
    "contact_us_successful_title";
  public static readonly contact_us_title = "contact_us_title";
  public static readonly mobile_payment_offer_popup_title =
    "mobile_payment_offer_popup_title";
  public static readonly mobile_payment_offer_popup_desc =
    "mobile_payment_offer_popup_desc";
  public static readonly mobile_payment_offer_popup_button =
    "mobile_payment_offer_popup_button";
  public static readonly mobile_payment_otp_popup_title =
    "mobile_payment_otp_popup_title";
  public static readonly mobile_payment_otp_popup_desc =
    "mobile_payment_otp_popup_desc";
  public static readonly mobile_payment_otp_popup_button =
    "mobile_payment_otp_popup_button";
  public static readonly otp_error_message = "otp_error_message";
  public static readonly otp_general_error_message =
    "otp_general_error_message";
  public static readonly otp_balance_error_message =
    "otp_balance_error_message";
  public static readonly delete_card_are_you_sure_title =
    "delete_card_are_you_sure_title";
  public static readonly delete_card_are_you_sure_desc =
    "delete_card_are_you_sure_desc";
  public static readonly delete_card_are_you_sure_button_delete =
    "delete_card_are_you_sure_button_delete";
  public static readonly delete_card_are_you_sure_button_cancel =
    "delete_card_are_you_sure_button_cancel";
  public static readonly error_daily_limit_popup_button =
    "error_daily_limit_popup_button";
  public static readonly error_daily_limit_popup_desc =
    "error_daily_limit_popup_desc";
  public static readonly error_daily_limit_popup_header =
    "error_daily_limit_popup_header";
  public static readonly error_mobile_payment_disabled_title =
    "error_mobile_payment_disabled_title";
  public static readonly error_mobile_payment_disabled_desc =
    "error_mobile_payment_disabled_desc";
  public static readonly error_mobile_payment_disabled_cc_button =
    "error_mobile_payment_disabled_cc_button";
  public static readonly error_mobile_payment_disabled_try_again_button =
    "error_mobile_payment_disabled_try_again_button";
  public static readonly error_mobile_payment_insufficient_limit_title =
    "error_mobile_payment_insufficient_limit_title";
  public static readonly error_mobile_payment_insufficient_limit_desc =
    "error_mobile_payment_insufficient_limit_desc";
  public static readonly error_mobile_payment_insufficient_limit_cc_button =
    "error_mobile_payment_insufficient_limit_cc_button";
  public static readonly error_mobile_payment_general_title =
    "error_mobile_payment_general_title";
  public static readonly error_mobile_payment_general_desc =
    "error_mobile_payment_general_desc";
  public static readonly error_mobile_payment_general_try_again_button =
    "error_mobile_payment_general_try_again_button";
  public static readonly error_mobile_payment_general_cc_button =
    "error_mobile_payment_general_cc_button";
  public static readonly error_cc_turkcell_title = "error_cc_turkcell_title";
  public static readonly error_cc_turkcell_desc = "error_cc_turkcell_desc";
  public static readonly error_cc_turkcell_mobile_payment_button =
    "error_cc_turkcell_mobile_payment_button";
  public static readonly error_cc_turkcell_try_again_button =
    "error_cc_turkcell_try_again_button";
  public static readonly error_payment_popup_header =
    "error_payment_popup_header";
  public static readonly error_payment_popup_desc = "error_payment_popup_desc";
  public static readonly error_payment_popup_button =
    "error_payment_popup_button";
  public static readonly faq = "faq";
  public static readonly faq_no_result = "faq_no_result";
  public static readonly faq_note = "faq_note";
  public static readonly faq_question_note = "faq_question_note";
  public static readonly faq_search = "faq_search";
  public static readonly header_contact_us = "header_contact_us";
  public static readonly header_help_desk = "header_help_desk";
  public static readonly home_button = "home_button";
  public static readonly home_title = "home_title";
  public static readonly onboarding_lottie_url = "onboarding_lottie_url";
  public static readonly home_newcomer_button = "home_newcomer_button";
  public static readonly home_newcomer_desc = "home_newcomer_desc";
  public static readonly home_newcomer_title = "home_newcomer_title";
  public static readonly question_list = "question_list";
  public static readonly redeem_reward_tool_tip = "redeem_reward_tool_tip";
  public static readonly save_card_header = "save_card_header";
  public static readonly save_card_description = "save_card_description";
  public static readonly cc_save_card_first_time_header =
    "cc_save_card_first_time_header";
  public static readonly cc_save_card_first_time_description =
    "cc_save_card_first_time_description";
  public static readonly cc_save_card_first_time_note =
    "cc_save_card_first_time_note";
  public static readonly cc_header_first_time_text =
    "cc_header_first_time_text";
  public static readonly cc_description_first_time_text =
    "cc_description_first_time_text";
  public static readonly invalid_token_title = "invalid_token_title";
  public static readonly invalid_token_text = "invalid_token_text";
  public static readonly offer_popup = "offer_popup";
  public static readonly leaderboard_header = "leaderboard_header";
  public static readonly leaderboard_title = "leaderboard_title";
  public static readonly leaderboard_desc = "leaderboard_desc";

  // EVENTS
  public static readonly user_count_homepage_view = "user_count_homepage_view";
  public static readonly user_count_startgame_clicked =
    "user_count_startgame_clicked";
  public static readonly user_count_answered = "user_count_answered";
  public static readonly user_count_1gb_won = "user_count_1gb_won";
  public static readonly user_count_500mb_won = "user_count_500mb_won";
  public static readonly user_count_250mb_won = "user_count_250mb_won";
  public static readonly user_count_wallet_clicked =
    "user_count_wallet_clicked";
  public static readonly offer_view_count = "offer_view_count";
  public static readonly user_count_playagain_button_clicked =
    "user_count_playagain_button_clicked";
  public static readonly user_count_saved_card_continue_click =
    "user_count_saved_card_continue_click";
  public static readonly user_count_1gb_surewin = "user_count_1gb_surewin";
  public static readonly user_count_4gb_surewin = "user_count_4gb_surewin";
  public static readonly user_count_direct_reward_button_clicked =
    "user_count_direct_reward_button_clicked";
  public static readonly user_count_leaderboard_button_clicked =
    "user_count_leaderboard_button_clicked";
  public static readonly free_offer_view_count = "free_offer_view_count";
  public static readonly free_user_count_continue_button_clicked =
    "free_user_count_continue_button_clicked";
  public static readonly user_count_save_card_continue_button_clicked =
    "user_count_save_card_continue_button_clicked";
  public static readonly user_count_charge_saved_card_continue_button_clicked =
    "user_count_charge_saved_card_continue_button_clicked";
  public static readonly user_count_big_reward_won =
    "user_count_big_reward_won";
  public static readonly user_count_mid_reward_won =
    "user_count_mid_reward_won";
  public static readonly user_count_small_prize_won =
    "user_count_small_prize_won";
  public static readonly user_count_continue_button_clicked =
    "user_count_continue_button_clicked";
  public static readonly user_count_offer_mobile_payment =
    "user_count_offer_mobile_payment";
  public static readonly user_count_offer_credit_card =
    "user_count_offer_credit_card";
  public static readonly user_count_offer_single = "user_count_offer_single";
  public static readonly user_count_offer_triple = "user_count_offer_triple";
}
